/**
 * use-share.tsx — hooks for the first-class shares model (spec: shares memory,
 * backend: worker/routes/share.js).
 *
 * Right now this file only carries `useShareEntry` — the /s/:token probe that
 * also handles the legacy `videos.share_token` fallback. Task 13 will grow it
 * to cover landing / folder-browse / watch queries + comment/approval/view/
 * download mutations paralleling use-watch.tsx.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, isApiError } from "@/lib/api";
import type { ApprovalStatus, StatusOption } from "@/lib/api-types";

// ── Owner-side share model ───────────────────────────────────────────────────

/**
 * One item in a share — a video or a folder.
 * Matches the payload shape POST /api/editor/shares expects.
 */
export type ShareItemRef = { type: "video" | "folder"; id: string };

/**
 * Body accepted by POST /api/editor/shares (backend: worker/routes/editor.js
 * — validated in validateShareItems). All settings are optional; server
 * defaults to 30-day expiry, download disabled, comments enabled, showAllVersions on.
 */
export type CreateShareBody = {
  items: ShareItemRef[];
  name?: string | null;
  expiresInDays?: number;
  expiresAt?: string;
  downloadAllowed?: boolean;
  commentAllowed?: boolean;
  showAllVersions?: boolean;
  /** Non-empty string sets a passphrase at creation (4–128 chars); null/omitted = none. */
  passphrase?: string | null;
  passphraseHint?: string | null;
};

/**
 * Response from POST /api/editor/shares. Frontend composes the /s/:token URL
 * from window.location.origin + shortToken.
 */
export type CreateShareResponse = {
  ok: true;
  id: string;
  shortToken: string;
  expiresAt: string;
  itemCount: number;
};

/**
 * Owner-side share row (GET /api/editor/shares result).
 */
export type ShareRow = {
  id: string;
  shortToken: string;
  projectId: string | null;
  name: string | null;
  expiresAt: string;
  downloadAllowed: boolean;
  commentAllowed: boolean;
  showAllVersions: boolean;
  visibilityEnabled: boolean;
  /** True when a passphrase is set. The hash itself is never sent to the client. */
  hasPassphrase: boolean;
  /** Optional hint shown to guests on the unlock screen. */
  passphraseHint?: string | null;
  viewCount: number;
  lastViewedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdByUserId: string;
  createdByName?: string | null;
  itemCount: number;
};

export type UpdateShareBody = Partial<{
  name: string | null;
  expiresAt: string;
  downloadAllowed: boolean;
  commentAllowed: boolean;
  showAllVersions: boolean;
  visibilityEnabled: boolean;
  /** Non-empty string sets/replaces the passphrase; null or "" clears it. */
  passphrase: string | null;
  passphraseHint: string | null;
}>;

/**
 * One guest visit on a share (GET /api/editor/shares/:id/visits — migration
 * 0018). Powers the Edit Share → Activity tab ("who opened this link").
 */
export type ShareVisit = {
  visitorId: string;
  name: string;
  email: string;
  firstSeen: string;
  lastSeen: string;
};

/** Activity tab: guest visitors who opened a share, newest first. */
export function useShareVisits(shareId: string | undefined) {
  return useQuery<{ shareId: string; visits: ShareVisit[] }>({
    queryKey: ["share", "visits", shareId],
    queryFn: () => api.get(`/editor/shares/${shareId}/visits`),
    enabled: !!shareId,
    staleTime: 30_000,
  });
}

// ── Shared shapes ────────────────────────────────────────────────────────────

/**
 * Landing payload from GET /api/share/:token.
 *
 * Matches the JSON produced by worker/routes/share.js. The `items` array is
 * heterogeneous — `type` discriminates between video and folder rows so the
 * frontend can render each with the right card. `singleAsset` is true only
 * when the share contains exactly one video (spec §"URL scheme": auto-lands
 * on the watch view).
 */
export type ShareLandingItemVideo = {
  type: "video";
  sortOrder: number;
  id: string;
  title: string;
  assetType?: "video" | "audio" | "image";
  approvalStatus?: ApprovalStatus;
  cdnUrl?: string | null;
  versionCount: number;
  /** Migration 0014 — REAL seconds. NULL for images + pre-0014 uploads. */
  durationSeconds?: number | null;
  /** Stack-wide comment count (root + all versions, includes replies). */
  commentCount?: number;
  createdAt?: string;
};

export type ShareLandingItemFolder = {
  type: "folder";
  sortOrder: number;
  id: string;
  name: string;
  folderCount: number;
  videoCount: number;
  createdAt?: string;
};

export type ShareLandingItem = ShareLandingItemVideo | ShareLandingItemFolder;

/** Workspace branding for guest pages (BR-1). All fields null = default Editlock. */
export type ShareBrand = {
  name: string | null;
  logoUrl: string | null;
  accent: string | null;
};

export type ShareLandingData = {
  ok: boolean;
  /**
   * Passphrase gate (CS-1). When true, the share is protected and this browser
   * hasn't cleared it yet — `items`/`skipForm`/`singleAsset` are absent and the
   * caller must render the unlock form instead of the grid.
   */
  passphraseRequired?: boolean;
  passphraseHint?: string | null;
  share: {
    id: string;
    shortToken: string;
    name: string | null;
    expiresAt?: string;
    downloadAllowed: boolean;
    commentAllowed: boolean;
    showAllVersions: boolean;
    viewCount?: number;
  };
  adminName?: string;
  brand?: ShareBrand | null;
  items: ShareLandingItem[];
  skipForm: boolean;
  singleAsset: boolean;
};

/**
 * Discriminator returned by useShareEntry.
 *
 *   'share'     — token resolved on the new /api/share endpoint (v0013+ shares).
 *                 Data payload is the full landing response.
 *   'legacy'    — token resolved on the old /api/watch endpoint (per-video
 *                 shareToken). Caller should navigate to /watch/:token.
 *   'not-found' — neither endpoint knows this token.
 *   'error'     — a non-404 error from at least one probe (network / 5xx).
 *                 Caller should surface a generic error, NOT redirect.
 */
export type ShareEntryResult =
  | { kind: "share"; data: ShareLandingData }
  | { kind: "legacy"; token: string }
  | { kind: "not-found" }
  | { kind: "error"; error: ApiError };

/**
 * /s/:token resolver. Probes the new shares endpoint first, falls back to the
 * legacy watch endpoint on a clean 404, and surfaces a real error on anything
 * else (network / 5xx). Never throws — always resolves to a discriminated union
 * so the calling page can branch on it.
 *
 * React Query caches by token; the caller can rely on a single probe per token
 * across mounts within the query's staleTime window.
 */
export function useShareEntry(token: string | undefined, preview = false) {
  const qs = preview ? "?preview=1" : "";
  return useQuery<ShareEntryResult>({
    queryKey: ["share", "entry", token, preview],
    enabled: !!token,
    // Token → resource identity mapping doesn't change fast. 5 min feels right;
    // rotate/revoke on the workspace side will just cause a stale success page
    // that 404s on any follow-up API call, which is fine UX.
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<ShareEntryResult> => {
      if (!token) return { kind: "not-found" };
      // Probe #1 — new-model shares.
      try {
        const data = await api.get<ShareLandingData>(`/share/${encodeURIComponent(token)}${qs}`);
        return { kind: "share", data };
      } catch (err) {
        if (!isApiError(err)) throw err;
        // 410 (expired) or 403 (disabled / workspace suspended) — this IS a
        // real hit on a new-model share, just gated. Do NOT fall through to
        // legacy — the token wouldn't be a legacy 32-char anyway and the
        // legacy probe would 404. Surface as error so the UI can show the
        // reason instead of a generic "not found".
        if (err.status !== 404) {
          return { kind: "error", error: err };
        }
        // else: 404 — try legacy fallback.
      }

      // Probe #2 — legacy /api/watch/:token (videos.share_token).
      try {
        await api.get<unknown>(`/watch/${encodeURIComponent(token)}`);
        return { kind: "legacy", token };
      } catch (err) {
        if (!isApiError(err)) throw err;
        if (err.status === 404) return { kind: "not-found" };
        return { kind: "error", error: err };
      }
    },
  });
}

/** One item inside a share, enriched with a display name (GET …/shares/:id). */
export type ShareDetailItem = {
  id: string;
  type: "video" | "folder";
  itemId: string;
  sortOrder: number;
  name: string;
  assetType?: "video" | "audio" | "image";
  cdnUrl?: string | null;
};

/** Full owner-side share detail + enriched items. */
export type ShareDetail = ShareRow & { items: ShareDetailItem[] };

/** GET /api/editor/shares/:id — owner detail + items for the Edit Share page. */
export function useShareDetail(shareId: string | undefined) {
  return useQuery<ShareDetail>({
    queryKey: ["share", "detail", shareId],
    queryFn: () => api.get<ShareDetail>(`/editor/shares/${shareId}`),
    enabled: !!shareId,
    staleTime: 15_000,
  });
}

/**
 * GET /api/editor/shares?projectId= — owner-side share list scoped to one
 * project (migration 0017). Powers the sidebar Share Links list + All Shares
 * table. Newest first (server reverses).
 */
export function useProjectShares(projectId: string | undefined) {
  return useQuery<ShareRow[]>({
    queryKey: ["shares", "project", projectId],
    queryFn: () =>
      api.get<ShareRow[]>(`/editor/shares?projectId=${encodeURIComponent(projectId!)}`),
    enabled: !!projectId,
    staleTime: 15_000,
  });
}

// ── Owner-side mutations ─────────────────────────────────────────────────────

/**
 * POST /api/editor/shares — create a new share row with the given items +
 * settings. Invalidates the workspace shares list.
 */
export function useCreateShare() {
  const qc = useQueryClient();
  return useMutation<CreateShareResponse, ApiError, CreateShareBody>({
    mutationFn: (body) => api.post<CreateShareResponse>("/editor/shares", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shares"] });
    },
  });
}

/**
 * PATCH /api/editor/shares/:id — update settings (name / expiry / toggles).
 * Owner-only. Kill switch = visibilityEnabled=false.
 */
export function useUpdateShare() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true },
    ApiError,
    { shareId: string; body: UpdateShareBody }
  >({
    mutationFn: ({ shareId, body }) =>
      api.patch<{ ok: true }>(`/editor/shares/${shareId}`, body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["shares"] });
      qc.invalidateQueries({ queryKey: ["share", "detail", vars.shareId] });
    },
  });
}

/**
 * DELETE /api/editor/shares/:id — permanent revoke. Cascades share_items on
 * the server. Old links start 404ing immediately.
 */
export function useDeleteShare() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, string>({
    mutationFn: (shareId) => api.delete<{ ok: true }>(`/editor/shares/${shareId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shares"] });
    },
  });
}

/**
 * POST /api/editor/shares/:id/rotate — mint a new short_token. Any existing
 * outstanding links stop resolving immediately.
 */
export function useRotateShare() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; shortToken: string },
    ApiError,
    string
  >({
    mutationFn: (shareId) =>
      api.post<{ ok: true; shortToken: string }>(`/editor/shares/${shareId}/rotate`),
    onSuccess: (_res, shareId) => {
      qc.invalidateQueries({ queryKey: ["shares"] });
      qc.invalidateQueries({ queryKey: ["share", "detail", shareId] });
    },
  });
}

/**
 * Build the guest-facing /s/:token URL from a share row / shortToken.
 * Kept as a helper so all callers agree on the URL scheme.
 */
export function buildShareUrl(shortToken: string): string {
  return `${window.location.origin}/s/${encodeURIComponent(shortToken)}`;
}

// ── Guest-side reads (Task 13) ───────────────────────────────────────────────

/**
 * Fetch the landing payload directly. Same shape as useShareEntry's success
 * variant but bypasses the legacy fallback probe — useful when the landing
 * page is opened directly without going through /s/:token entry (e.g., from
 * a share-scoped sub-page that needs to know the share settings).
 *
 * useShareEntry is the right hook for the FIRST hit on /s/:token; this is for
 * sub-pages that already know the token resolves to a new-model share.
 */
export function useShareLanding(token: string | undefined, preview = false) {
  const qs = preview ? "?preview=1" : "";
  return useQuery<ShareLandingData, ApiError>({
    queryKey: ["share", "landing", token, preview],
    enabled: !!token,
    staleTime: 60 * 1000,   // landing changes on identify + view — refresh window
    retry: false,
    queryFn: () => api.get<ShareLandingData>(`/share/${encodeURIComponent(token!)}${qs}`),
  });
}

/**
 * Folder contents inside a share. Backend: GET /api/share/:token/folders/:id.
 */
export type ShareFolderVideo = {
  id: string;
  title: string;
  assetType?: "video" | "audio" | "image";
  approvalStatus?: ApprovalStatus;
  cdnUrl?: string | null;
  /** Migration 0014 — REAL seconds. NULL for images + pre-0014 uploads. */
  durationSeconds?: number | null;
  /** Stack-wide comment count (root + all versions, includes replies). */
  commentCount?: number;
  createdAt?: string;
};

export type ShareFolderSubfolder = {
  id: string;
  name: string;
  folderCount: number;
  videoCount: number;
  createdAt?: string;
};

export type ShareFolderData = {
  ok: true;
  folder: {
    id: string;
    name: string;
    parentFolderId: string | null;
  };
  folders: ShareFolderSubfolder[];
  videos: ShareFolderVideo[];
};

export function useShareFolder(token: string | undefined, folderId: string | undefined) {
  return useQuery<ShareFolderData, ApiError>({
    queryKey: ["share", "folder", token, folderId],
    enabled: !!token && !!folderId,
    staleTime: 30 * 1000,
    retry: false,
    queryFn: () =>
      api.get<ShareFolderData>(
        `/share/${encodeURIComponent(token!)}/folders/${encodeURIComponent(folderId!)}`
      ),
  });
}

// ── Guest identify (visitor cookie mint) ─────────────────────────────────────

/**
 * POST /api/share/:token/identify — captures guest name+email and sets a 24h
 * visitor_id cookie server-side. On success we invalidate landing so skipForm
 * flips to true and any card-hover states re-render.
 */
// ── Guest watch (Chunk B) ────────────────────────────────────────────────────

/**
 * GET /api/share/:token/videos/:videoId — primary loader for the guest watch
 * page. Shape mirrors GET /api/watch/:token but scoped by share + includes
 * `shareSettings` so the frontend knows what to gate on.
 */
export type ShareWatchVersion = {
  id: string;
  versionNumber: number;
  assetType?: "video" | "audio" | "image";
  approvalStatus?: ApprovalStatus;
  cdnUrl?: string | null;
  createdAt?: string;
  /** External-link assets (migration 0024). 'upload' = hosted file (use cdnUrl). */
  sourceType?: "upload" | "youtube" | "vimeo";
  sourceVideoId?: string | null;
  sourceUrl?: string | null;
};

export type ShareVideoData = {
  ok: true;
  skipForm: boolean;
  rootVideoId: string;
  video: {
    id: string;
    title: string;
    assetType?: "video" | "audio" | "image";
    versionNumber: number;
    cdnUrl?: string | null;
    sourceType?: "upload" | "youtube" | "vimeo";
    sourceVideoId?: string | null;
    sourceUrl?: string | null;
    downloadAllowed?: boolean;
    approvalStatus?: ApprovalStatus;
    approvalUpdatedAt?: string;
  };
  versions: ShareWatchVersion[];
  adminName?: string;
  statusOptions?: StatusOption[];
  status?: StatusOption | null;
  shareSettings: {
    commentAllowed: boolean;
    downloadAllowed: boolean;
    showAllVersions: boolean;
  };
};

export function useShareVideo(token: string | undefined, videoId: string | undefined, preview = false) {
  const qs = preview ? "?preview=1" : "";
  return useQuery<ShareVideoData, ApiError>({
    queryKey: ["share", "video", token, videoId, preview],
    enabled: !!token && !!videoId,
    staleTime: 15_000,
    retry: false,
    queryFn: () =>
      api.get<ShareVideoData>(
        `/share/${encodeURIComponent(token!)}/videos/${encodeURIComponent(videoId!)}${qs}`
      ),
  });
}

export function useShareIdentify(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { ok: true },
    ApiError,
    { name: string; email: string }
  >({
    mutationFn: (body) => {
      if (!token) throw new ApiError(0, "Missing share token");
      return api.post<{ ok: true }>(`/share/${encodeURIComponent(token)}/identify`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["share", "landing", token] });
      qc.invalidateQueries({ queryKey: ["share", "entry", token] });
    },
  });
}

/**
 * POST /api/share/:token/passphrase — unlock a passphrase-protected share.
 * The server sets a 12h clearance cookie on success. We invalidate every
 * token-scoped guest query so the gated entry/landing/video refetch and pass
 * the gate on the next render.
 */
export function useShareUnlock(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, ApiError, { passphrase: string }>({
    mutationFn: (body) => {
      if (!token) throw new ApiError(0, "Missing share token");
      return api.post<{ ok: true }>(`/share/${encodeURIComponent(token)}/passphrase`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["share", "entry", token] });
      qc.invalidateQueries({ queryKey: ["share", "landing", token] });
      qc.invalidateQueries({ queryKey: ["share", "video", token] });
    },
  });
}
